/**
 * ktg-variant-value-hider.js
 *
 * Hides option values (size/color/etc radio, swatch, or dropdown items) that
 * do not exist as ANY variant combination for the product's UPSTREAM
 * (earlier-position) currently selected options — as opposed to the theme's
 * built-in SelloutVariants, which only adds a strike-through ".unavailable"
 * class but leaves the value selectable.
 *
 * IMPORTANT — option order matters: each option is filtered only by the
 * options that come BEFORE it (lower position). A later, dependent option
 * (e.g. "Leg Style", whose valid values depend on Size) never restricts an
 * earlier, independent option (e.g. Size or Color). This requires the
 * product's options to be ordered "independent -> dependent" in Shopify
 * Admin (e.g. Color, then Size, then Leg Style) — the same convention
 * Shopify itself recommends for multi-option products. If options are
 * ordered the other way, an independent option could be wrongly hidden by
 * a dependent option's stale value.
 *
 * Sold-out-but-valid combinations (variant exists, available: false) are
 * intentionally left visible/selectable so customers can still see the
 * combination is real, just out of stock — theme.js already marks those
 * with the "sold-out" class and diagonal-line styling.
 *
 * Scope: main product page only (main-product form rendered via
 * snippets/product-variant-options.liquid + snippets/product-buttons.liquid).
 * Deliberately does NOT run inside the quickview/popdown modals, since
 * those are reached from collection/quick-add contexts.
 *
 * Depends on nothing from theme.js — reads the same embedded
 * [data-product-json] payload independently, so it keeps working even if
 * theme.js internals change on a future theme update.
 */
(function () {
  'use strict';

  var HIDDEN_CLASS = 'ktg-hide-container';

  // Values that stand in for "no real choice here" (e.g. Leg Style's
  // "Standard" for sizes that don't offer A/B). Only when the SOLE
  // remaining valid value for an option matches one of these names does
  // the whole option row get hidden and auto-selected. A genuine
  // single-value option whose one value isn't in this list (whether it
  // only ever had one value, or upstream selections happened to narrow
  // it down to one) stays visible instead of silently disappearing.
  // Add merchant-specific placeholder names here as needed.
  var PLACEHOLDER_VALUES = ['standard'];

  function isPlaceholderValue(value) {
    return PLACEHOLDER_VALUES.indexOf(String(value).trim().toLowerCase()) !== -1;
  }

  var SELECTORS = {
    form: '[data-product-form]',
    productJson: '[data-product-json]',
    optionWrapper: '[data-swapper-wrapper][data-option-position]',
    optionInput: 'input[name^="options"]',
    popoutInput: 'input[data-popout-input]',
    popoutOption: 'a[data-popout-option]',
  };

  function parseProductJSON(root) {
    var el = root.querySelector(SELECTORS.productJson);
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function getOptionWrappers(root) {
    return Array.prototype.slice.call(root.querySelectorAll(SELECTORS.optionWrapper));
  }

  // Read the value currently selected within one option wrapper
  // (works for both radio/swatch style and popout/dropdown style).
  function getWrapperCurrentValue(wrapper) {
    var checkedRadio = wrapper.querySelector(SELECTORS.optionInput + ':checked');
    if (checkedRadio) return checkedRadio.value;

    var popoutInput = wrapper.querySelector(SELECTORS.popoutInput);
    if (popoutInput) return popoutInput.value;

    return null;
  }

  // Every clickable "candidate value" element within a wrapper, each
  // tagged with the DOM node that should be hidden (its visual container),
  // the element theme.js's own SelloutVariants marks with .unavailable/
  // .sold-out (the input/anchor itself), and the raw option value it
  // represents.
  function getWrapperCandidates(wrapper) {
    var candidates = [];

    // Swatch or plain radio buttons
    var radios = wrapper.querySelectorAll(SELECTORS.optionInput);
    radios.forEach(function (input) {
      // radio-swatch custom element, or span.radio__button wrapper
      var container = input.closest('radio-swatch') || input.closest('.radio__button');
      if (container) {
        candidates.push({value: input.value, container: container, element: input});
      }
    });

    // Dropdown / popout list items
    var popoutOptions = wrapper.querySelectorAll(SELECTORS.popoutOption);
    popoutOptions.forEach(function (anchor) {
      var container = anchor.closest('li');
      if (container) {
        candidates.push({value: anchor.getAttribute('data-value'), container: container, element: anchor});
      }
    });

    return candidates;
  }

  function initProductForm(root) {
    var form = root.querySelector(SELECTORS.form);
    var productJSON = parseProductJSON(root);
    if (!form || !productJSON || !productJSON.variants || !productJSON.variants.length) return;

    var wrappers = getOptionWrappers(root);
    if (wrappers.length < 2) return; // nothing to filter with a single option

    // Simulate a real user selection so the theme's own listeners
    // (SelloutVariants, price/image updates, add-to-cart state) run
    // exactly as they would from a genuine click — rather than
    // duplicating that logic ourselves.
    function forceSelectValue(wrapper, value) {
      var radio = wrapper.querySelector(SELECTORS.optionInput + '[value="' + CSS.escape(value) + '"]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', {bubbles: true}));
        return;
      }

      // Dropdown/popout style: click the matching <a data-popout-option>
      // so PopoutSelect's own handler updates its value, visible label,
      // and "current" state consistently (it has no external sync path).
      var anchor = wrapper.querySelector(SELECTORS.popoutOption + '[data-value="' + CSS.escape(value) + '"]');
      if (anchor) {
        anchor.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true}));
      }
    }

    var MAX_PASSES = 4; // settles quickly for up to ~3 options; guards against loops

    function update(pass) {
      pass = pass || 0;

      // Progressive/cascading context: each option's valid values are
      // determined only by the options BEFORE it (lower position index),
      // never by options after it. This matters because a downstream,
      // dependent option (e.g. Leg Style, whose values depend on Size)
      // must never veto an upstream, independent option (e.g. Size or
      // Color) just because the downstream option's current value
      // happens to be stale for a hypothetical change to the upstream
      // option. Requires that the product's options are actually
      // ordered "independent -> dependent" in Shopify Admin (e.g.
      // Color, Size, Leg Style) — same convention Shopify itself
      // recommends for multi-option products.
      var context = [];
      var correctionNeeded = null; // {wrapper, value} — at most one per pass

      wrappers.forEach(function (wrapper, position) {
        var candidates = getWrapperCandidates(wrapper);
        var visible = [];

        candidates.forEach(function (candidate) {
          var matches = productJSON.variants.filter(function (variant) {
            for (var i = 0; i < position; i++) {
              if (variant.options[i] !== context[i]) return false;
            }
            return variant.options[position] === candidate.value;
          });

          // No variant exists at all for this combination -> hide it.
          // A variant exists but is sold out -> leave visible (theme.js
          // already applies the sold-out / strike-through styling).
          if (!matches.length) {
            candidate.container.classList.add(HIDDEN_CLASS);
          } else {
            candidate.container.classList.remove(HIDDEN_CLASS);
            visible.push(candidate);

            // Correct theme.js's own SelloutVariants marking (assets/theme.dev.js
            // "Precrime" check): it decides .unavailable/.sold-out by holding
            // every OTHER option at its CURRENT value, including options AFTER
            // this one. So once a downstream option (e.g. Leg Style) gets
            // corrected to a value that only exists for a different upstream
            // choice, SelloutVariants wrongly crosses out this upstream value
            // (e.g. Size "36" looks unavailable just because Leg Style is
            // currently "Standard"). Recompute using only upstream context
            // (ignoring downstream positions, same as `matches` above) and
            // force the correct classes on the actual input/anchor element.
            var anyAvailable = matches.some(function (variant) {
              return variant.available;
            });
            candidate.element.classList.remove('unavailable');
            candidate.element.classList.toggle('sold-out', !anyAvailable);
          }
        });

        // Collapse the entire option (e.g. "Leg Style") only when the
        // upstream selections leave a single PLACEHOLDER value (see
        // PLACEHOLDER_VALUES) as the sole choice — that's "nothing for
        // the customer to decide". A single non-placeholder value (a
        // genuinely one-value option, or one narrowed to one real
        // choice) is left visible so its value stays visible/selectable.
        var onlyPlaceholderLeft = visible.length === 1 && isPlaceholderValue(visible[0].value);
        if (visible.length === 0 || onlyPlaceholderLeft) {
          wrapper.classList.add(HIDDEN_CLASS);
        } else {
          wrapper.classList.remove(HIDDEN_CLASS);
        }

        // If what's currently selected in this option is no longer a
        // visible/valid choice given the upstream context, queue a
        // correction to the first remaining visible value. Only handle
        // one correction per pass, then re-run update() so the
        // correction's downstream effects get evaluated with fresh data.
        var currentValue = getWrapperCurrentValue(wrapper);
        var stillValid = visible.some(function (c) {
          return c.value === currentValue;
        });

        var decidedValue = currentValue;
        if (!stillValid && visible.length > 0) {
          decidedValue = visible[0].value;
          if (!correctionNeeded) {
            correctionNeeded = {wrapper: wrapper, value: decidedValue};
          }
        }

        // This position's decided value becomes fixed context for
        // every position after it.
        context.push(decidedValue);
      });

      if (correctionNeeded && pass < MAX_PASSES) {
        forceSelectValue(correctionNeeded.wrapper, correctionNeeded.value);
        update(pass + 1);
      }
    }

    // Attach directly to each option input (not delegated), matching how
    // theme.js's own ProductForm._initInputs binds listeners — this is
    // required because the popout/dropdown's hidden input change event
    // is dispatched programmatically and does not necessarily bubble.
    //
    // Queried from `root`, not `form`: the option inputs rendered by
    // snippets/product-variant-options.liquid are NOT DOM descendants of
    // the <form data-product-form> element (that form opens/closes
    // entirely inside snippets/product-buttons.liquid, rendered earlier)
    // — they're linked to it only via the HTML `form="{{ uniq_id }}"`
    // attribute. form.querySelectorAll(...) would therefore find none of
    // them and silently attach zero listeners.
    var allOptionInputs = root.querySelectorAll(SELECTORS.optionInput + ', ' + SELECTORS.popoutInput);
    allOptionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        update(); // no args — avoids passing the Event into the pass-counter
      });
    });

    // Initial pass on load, in case a default/selected variant already
    // has values elsewhere that would be invalid to switch to.
    update();
  }

  function init() {
    // [data-product-form-wrapper] is the real wrapper rendered by both
    // sections/product.liquid and sections/section-product.liquid, and is
    // the same attribute theme.js itself already relies on
    // (selectors$m.productFormWrapper). It is also reused inside the
    // quickview modal (rendered via snippets/product-clip.liquid /
    // product-grid-item-variant.liquid), so we explicitly exclude any
    // wrapper that lives inside [data-quickview-modal] to keep this
    // scoped to the main product page only, per requirements.
    var roots = Array.prototype.slice
      .call(document.querySelectorAll('[data-product-form-wrapper]'))
      .filter(function (root) {
        return !root.closest('[data-quickview-modal]');
      });

    roots.forEach(initProductForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();